import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { RootState, AppDispatch } from 'store/store';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { TABLE_HEIGHT } from 'constants/ui';
import { formatFullName, hasPermission, statusKeyMap } from 'utils/utils';
import { GuestPass } from 'types/guestPassTypes';
import { getRequest } from 'api/apiClient';
import { getStatusNameById } from 'utils/visitorsHelper';
import { fetchVisits, setSelectedVisit, setVisitsEndDate, setVisitsStartDate } from 'store/slices/visitsSlice';
import VisitorsDrawer from 'components/pages/visitors/drawer/VisitorsDrawer';
import DateRangeLocationFilter2 from 'components/customAntd/DateRangeLocationFilter2';
import { Location } from 'types/locationTypes';
import { fetchLocations } from 'store/slices/locationsSlice';
import { useTranslation } from 'react-i18next';
import { VisitStatus } from 'types/visitTypes';

dayjs.extend(isBetween);

interface GuestPassReportTableData {
    id: number;
    visitId: number;
    visitor: string;
    status: string;
    validFrom: string;
    validTo: string;
}

const GuestPassReportTable: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const triggerUpdate = useSelector((state: RootState) => state.guestPass.triggerUpdate);
    const { locations } = useSelector((state: RootState) => state.locations);

    const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
    const [selectedLocationId, setSelectedLocationId] = useState<number>(globalLocationId!);
    const [startDate, setStartDate] = useState(dayjs().startOf('week').utc());
    const [endDate, setEndDate] = useState(dayjs().endOf('week').utc());
    const [loading, setLoading] = useState<boolean>(false);
    const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
    const [statusFilterOptions, setStatusFilterOptions] = useState<{ label: string; value: string }[]>([]);
    const [defaultStatusFilter, setDefaultStatusFilter] = useState<string[]>([]);

    const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState(false);
    const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);

    const hasAllvisitorsRead = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:r');
    const hasGuestpassRead = hasPermission(tokenScopeList, orgId, 'o', 'guestpass:r');

    const { t } = useTranslation();

    // Memoized fetchGuestPasses function
    const fetchGuestPasses = useCallback(async () => {
        setLoading(true);
        try {
            const guestPassResponse = await getRequest(`/orgs/${orgId}/msiAltaGuestPassByDate`, {
                startDate: dayjs(startDate).format(DATE_TIME_FORMAT),
                endDate: dayjs(endDate).format(DATE_TIME_FORMAT),
                status: 1
            });

            // Fetch locations and extract .data
            if (orgId && locations.length === 0) {
                dispatch(fetchLocations({ orgId, status: 1 }));
            }

            const filteredGuestPasses = guestPassResponse.data.filter((guestPass: GuestPass) => {
                const matchesLocation =
                    (selectedLocationId === -1 || guestPass.siteId === selectedLocationId) &&
                    locations.some((location: Location) => location.id === guestPass.siteId);

                return matchesLocation && (hasAllvisitorsRead || globalUserId === guestPass.hostUserId) && guestPass.visitStatus !== 0;
            });
            setGuestPasses(filteredGuestPasses);
        } catch (error) {
            console.log("Failed to fetch guest passes.", error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, locations, orgId, startDate, endDate, selectedLocationId, hasAllvisitorsRead, globalUserId]);

    // Memoized fetchVisitsData function
    const fetchVisitsData = useCallback(async () => {
        await dispatch(setVisitsStartDate(undefined));
        await dispatch(setVisitsEndDate(undefined));
        await dispatch(fetchVisits({ orgId }));
    }, [orgId, dispatch]);

    useEffect(() => {
        fetchGuestPasses();
        fetchVisitsData();
    }, [fetchGuestPasses, fetchVisitsData, startDate, endDate, triggerUpdate]);

    useEffect(() => {
        const fetchVisitStatuses = async () => {
            try {
                const visitorStatus = await getRequest(`/orgs/${orgId}/visitorStatus`);
                setVisitStatus(visitorStatus.data);
            } catch (e) {
                console.error('Failed to fetch visit statuses', e);
            }
        };

        if (orgId) {
            fetchVisitStatuses();
        }
    }, [orgId]);

    useEffect(() => {
        if (visitStatus.length > 0) {
            const options = [
                ...visitStatus.map(status => ({
                    label: t(status.languageCode ?? '', status.name),
                    value: status.name,
                })),
                {
                    label: t('vst_data_noshow', 'No Show'),
                    value: 'No Show',
                },
            ];

            setStatusFilterOptions(options);
            setDefaultStatusFilter(options.map(option => option.value));
        }
    }, [visitStatus, t]);



    // Table columns definition
    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'visitor',
            label: t('guestPassReport.table.visitor'),
            filter: { type: 'input' },
            sorter: (a, b) => a.visitor.length - b.visitor.length
        },
        {
            dataIndex: 'status',
            label: t('guestPassReport.table.status'),
            filter: {
                type: 'multiSelect',
                options: statusFilterOptions,
            },

            defaultFilteredValue: defaultStatusFilter,
            sorter: (a, b) => a.status.localeCompare(b.status),
            onFilter: (value, record) => record.status === value,
            render: (value) => t(statusKeyMap[value] || value),
        },
        {
            dataIndex: 'passStatus',
            label: t('guestPassReport.table.passStatus'),
            filter: {
                type: 'multiSelect',
                options: [
                    { label: t('guestPassReport.table.valid'), value: 'Valid' },
                    { label: t('guestPassReport.table.invalid'), value: 'Invalid' },
                ],
            },
            defaultFilteredValue: ['Valid', 'Invalid'],
            sorter: (a, b) => a.passStatus.localeCompare(b.passStatus),
            onFilter: (value, record) => {
                return record.passStatus === value;
            },
            render: (value) => {
                const color = value === 'Valid' ? '#709A7A' : 'grey';
                const textColor = 'white';
                const translated = value === 'Valid' ? t('guestPassReport.table.valid') : t('guestPassReport.table.invalid');

                return (
                    <div
                        style={{
                            backgroundColor: color,
                            color: textColor,
                            padding: '5px 10px',
                            borderRadius: '4px',
                            textAlign: 'center',
                        }}
                    >
                        {translated}
                    </div>
                );
            },
        },
        {
            dataIndex: 'validFrom',
            label: t('guestPassReport.table.validFrom'),
            sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime)
        },
        {
            dataIndex: 'validTo',
            label: t('guestPassReport.table.validTo'),
            sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime)
        },
        {
            dataIndex: 'createdAt',
            label: t('guestPassReport.table.createdAt'),
            sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
            hidden: true
        },
        {
            dataIndex: 'updatedAt',
            label: t('guestPassReport.table.updatedAt'),
            sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
            hidden: true
        },
        {
            dataIndex: 'location',
            label: t('guestPassReport.table.location'),
            filter: {
                type: 'multiSelect',
                options: locations.map(location => ({
                    label: location.name,
                    value: location.id,
                })),
            },
            defaultFilteredValue: locations.map(location => String(location.id)),
            sorter: (a, b) => a.location.localeCompare(b.location),
            onFilter: (value, record) => {
                return String(record.siteId) === String(value)
            },
            hidden: true
        },
        {
            dataIndex: 'issuer',
            label: t('guestPassReport.table.issuer'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.issuer || '').localeCompare(b.issuer || ''),
            hidden: true
        },
    ];

    const handleDateRangeLocationFilter = (locationId: number, startDate: string, endDate: string) => {
        setSelectedLocationId(locationId);
        setStartDate(dayjs(startDate).utc());
        setEndDate(dayjs(endDate).utc());
    };

    const getPassStatus = (startUtc: string, endUtc: string): string => {
        const now = dayjs();
        return now.isBetween(dayjs(startUtc).utc(true), dayjs(endUtc).utc(true), null, '[]') ? 'Valid' : 'Invalid';
    };

    // Format activities for table display
    const formatGuestPassesReportForTable = (guestPasses: GuestPass[]): GuestPassReportTableData[] => {
        return guestPasses.map((guestPass) => {
            const location = locations.find(location => location.id === guestPass.siteId);
            const generatedBy = guestPass.generatedBy?.[0];
            const issuerName = generatedBy ? formatFullName(generatedBy.firstName, null, generatedBy.lastName) : '';

            return {
                id: guestPass.id,
                visitId: guestPass.visitId,
                visitor: formatFullName(guestPass.visitor[0]?.firstName, guestPass.visitor[0]?.middleName, guestPass.visitor[0]?.lastName)
                    + (guestPass.visitor.length > 1 ? ` (+${guestPass.visitor.length - 1})` : ''),
                status: getStatusNameById(guestPass.visitStatus, undefined),
                validFrom: dayjs(guestPass.startUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                validTo: dayjs(guestPass.endUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                passStatus: getPassStatus(guestPass.startUtc, guestPass.endUtc),
                location: location?.name,
                createdAt: dayjs(guestPass.createdAt).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                updatedAt: dayjs(guestPass.updatedAt).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                siteId: guestPass.siteId,
                issuer: issuerName,
            };
        });
    };

    const handleVisitClick = async (visit: any) => {
        const response = await getRequest(`/orgs/${orgId}/visitor/${visit.visitId}`);
        const selectedVisit = response.data[0];
        dispatch(setSelectedVisit(selectedVisit!));
        setIsVisitorsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsVisitorsDrawerOpen(false);
        dispatch(setSelectedVisit(null));
    };

    // Table properties
    const opTableProps: IOpTableProps = {
        dataSource: formatGuestPassesReportForTable(guestPasses),
        label: t('guestPassReport.table.label'),
        columns: columns,
        rowActions: {
            onEditClick: handleVisitClick,
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        allowExport: true,
        allowShowHideColumns: true,
        loading: loading,
        gtm: 'activity-log-table-gtm',
        rowKey: 'id',
    };

    if (statusFilterOptions.length === 0 || defaultStatusFilter.length === 0) {
        return null;
    }

    return (
        <OpSpace direction="vertical" size="middle" style={{ display: 'flex' }}>
            <DateRangeLocationFilter2
                onDateRangeLocationFilter={handleDateRangeLocationFilter}
                initialStartDate={startDate.utc(true).local().format(DATE_TIME_FORMAT)}
                initialEndDate={endDate.utc(true).local().format(DATE_TIME_FORMAT)}
            />

            <OpTable {...opTableProps} />

            {(isVisitorsDrawerOpen) && (
                <VisitorsDrawer
                    open={isVisitorsDrawerOpen}
                    isProfileDrawerOpen={isProfileDrawerOpen}
                    onClose={handleDrawerClose}
                    setIsProfileDrawerOpen={setIsProfileDrawerOpen}
                    tab={hasGuestpassRead ? 'guestPass' : 'visitors'}
                />
            )}
        </OpSpace>
    );
}

export default GuestPassReportTable;